"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, Wrench, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLanguage } from "./LanguageProvider";
import { HYUNDAI_NEW_STOCK_URL } from "@/lib/inventoryLinks";

type NavLink = {
  label: string;
  href: string;
};

type NavItem =
  | { type: "link"; label: string; href: string }
  | { type: "dropdown"; label: string; children: NavLink[] };

const getNavItems = (language: "sv" | "en"): NavItem[] => {
  const inventory: NavItem = {
    type: "dropdown",
    label: language === "sv" ? "Bilar i lager" : "Cars in stock",
    children: [
      {
        label: language === "sv" ? "Begagnade bilar" : "Used cars",
        href: "/fordon-i-lager",
      },
      {
        label: language === "sv" ? "Nya Hyundai bilar" : "New Hyundai cars",
        href: HYUNDAI_NEW_STOCK_URL,
      },
    ],
  };

  if (language === "en") {
    return [
      { type: "link", label: "Campaigns", href: "/kampanjer" },
      { type: "link", label: "Mazda", href: "/mazda" },
      { type: "link", label: "Hyundai", href: "/hyundai" },
      inventory,
      { type: "link", label: "Workshop", href: "/verkstad" },
      { type: "link", label: "About us", href: "/om-oss" },
    ];
  }

  return [
    { type: "link", label: "Kampanjer", href: "/kampanjer" },
    { type: "link", label: "Mazda", href: "/mazda" },
    { type: "link", label: "Hyundai", href: "/hyundai" },
    inventory,
    { type: "link", label: "Verkstad", href: "/verkstad" },
    { type: "link", label: "Om oss", href: "/om-oss" },
  ];
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const pathname = usePathname();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && typeof window !== "undefined" && !sessionStorage.getItem("navbar-animated")) {
      setShouldAnimate(true);
    }
  }, [mounted]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useLayoutEffect(() => {
    if (shouldAnimate && typeof window !== "undefined") {
      sessionStorage.setItem("navbar-animated", "true");
    }
  }, [shouldAnimate]);

  /** Pages with hero (video or image): transparent header before scroll, solid after */
  const path = pathname ?? "";
  const isHeroPage =
    path === "/" ||
    path === "/mazda" ||
    path === "/hyundai" ||
    path === "/kampanjer" ||
    path === "/verkstad" ||
    path === "/om-oss" ||
    path.startsWith("/event/") ||
    /^\/kampanjer\/[^/]+\/[^/]+$/.test(path);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);

    if (href.startsWith("/#") && path === "/") {
      const hash = href.substring(1);
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = getNavItems(language);
  const isLightNav = isScrolled || !isHeroPage;

  const renderLink = (link: NavLink, className: string) => {
    const isExternal = link.href.startsWith("http");

    if (isExternal) {
      return (
        <a
          href={link.href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        href={link.href}
        className={className}
        onClick={() => handleNavClick(link.href)}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <>
      <motion.nav
        key={mounted && shouldAnimate ? "animate" : "static"}
        initial={mounted && shouldAnimate ? { y: -100 } : false}
        animate={{ y: 0 }}
        transition={mounted && shouldAnimate ? { duration: 0.6, ease: "easeOut" } : { duration: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || !isHeroPage
            ? "bg-background/95 backdrop-blur-md shadow-lg py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="relative z-10">
            <Image
              src="/assets/logo_bilspektrum.png"
              alt="Bilspektrum AB"
              width={440}
              height={220}
              sizes="(max-width: 768px) 328px, (max-width: 1024px) 400px, 440px"
              className={`h-8 md:h-10 lg:h-9 xl:h-12 w-auto transition-all duration-300 ${
                isScrolled || !isHeroPage ? "h-7 md:h-8 lg:h-8 xl:h-10" : "brightness-0 invert"
              }`}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-7 whitespace-nowrap mx-6">
            {navItems.map((item) => {
              if (item.type === "link") {
                return (
                  <span key={item.label}>
                    {renderLink(
                      { label: item.label, href: item.href },
                      `nav-link text-sm font-medium transition-colors duration-300 ${
                        isLightNav
                          ? "text-foreground hover:text-primary"
                          : "text-white hover:text-white/80"
                      }`
                    )}
                  </span>
                );
              }
              const isOpen = openDropdown === item.label;

              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    type="button"
                    className={`nav-link flex items-center gap-1 text-sm font-medium transition-colors duration-300 ${
                      isLightNav
                        ? "text-foreground hover:text-primary"
                        : "text-white hover:text-white/80"
                    }`}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 pt-2 min-w-[160px]"
                      >
                        <div className="rounded-lg border border-border bg-background py-2 shadow-lg">
                          {item.children.map((child) => {
                            const isExternal = child.href.startsWith("http");
                            return isExternal ? (
                              <a
                                key={child.label}
                                href={child.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                key={child.label}
                                href={child.href}
                                className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                onClick={() => setOpenDropdown(null)}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-3 whitespace-nowrap">
            <button
              type="button"
              onClick={toggleLanguage}
              className={`px-1.5 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                isLightNav
                  ? "border-border text-foreground bg-background/60"
                  : "border-white/50 text-white bg-white/5"
              }`}
            >
              <span
                className={`px-2 py-0.5 rounded-full ${
                  language === "sv"
                    ? "bg-primary text-primary-foreground"
                    : isLightNav
                      ? "text-foreground/70"
                      : "text-white/70"
                }`}
              >
                SV
              </span>
              <span
                className={`px-2 py-0.5 rounded-full ${
                  language === "en"
                    ? "bg-primary text-primary-foreground"
                    : isLightNav
                      ? "text-foreground/70"
                      : "text-white/70"
                }`}
              >
                EN
              </span>
            </button>
            <Link
              href="/boka-verkstad"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium text-sm border transition-all duration-300 ${
                isScrolled || !isHeroPage
                  ? "border-primary/30 text-foreground hover:bg-primary/5"
                  : "border-white/50 text-white hover:bg-white/10"
              }`}
            >
              <Wrench className="w-4 h-4" />
              {language === "sv" ? "Boka service" : "Book service"}
            </Link>
            <a
              href="tel:090-120400"
              aria-label="Ring oss på 090-12 04 00"
              className={`flex items-center gap-2 px-3 2xl:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                isScrolled || !isHeroPage
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-white text-primary hover:bg-white/90"
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden 2xl:inline">Ring oss</span>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden relative z-10 p-2 ${
              isScrolled || !isHeroPage ? "text-foreground" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-primary pt-24 px-6"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-white/80">
                  {language === "sv"
                    ? "Välj språk"
                    : "Choose language"}
                </span>
                <button
                  type="button"
                  onClick={toggleLanguage}
                  className="px-1.5 py-1 rounded-full text-xs font-semibold border border-white/60 text-white bg-white/5 flex items-center gap-1.5"
                >
                  <span
                    className={`px-2 py-0.5 rounded-full ${
                      language === "sv"
                        ? "bg-primary text-primary-foreground"
                        : "text-white/70"
                    }`}
                  >
                    SV
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full ${
                      language === "en"
                        ? "bg-primary text-primary-foreground"
                        : "text-white/70"
                    }`}
                  >
                    EN
                  </span>
                </button>
              </div>
              {navItems.map((item, index) => {
                if (item.type === "link") {
                  return (
                    <motion.span
                      key={item.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {renderLink(
                        { label: item.label, href: item.href },
                        "text-2xl font-display text-white hover:text-white/80 transition-colors block"
                      )}
                    </motion.span>
                  );
                }
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-t border-white/20 pt-4 mt-2"
                  >
                    <p className="text-sm font-medium text-white/70 uppercase tracking-wider mb-3">
                      {item.label}
                    </p>
                    <div className="flex flex-col gap-2 pl-2">
                      {item.children.map((child) => {
                        const isExternal = child.href.startsWith("http");
                        return isExternal ? (
                          <a
                            key={child.label}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-xl font-display text-white hover:text-white/80 transition-colors"
                          >
                            {child.label}
                          </a>
                        ) : (
                          <Link
                            key={child.label}
                            href={child.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-xl font-display text-white hover:text-white/80 transition-colors"
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.1 }}
                className="mt-6"
              >
                <Link
                  href="/boka-verkstad"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-base font-semibold text-primary"
                >
                  <Wrench className="w-5 h-5" />
                  {language === "sv" ? "Boka service" : "Book service"}
                </Link>
              </motion.div>
              <motion.a
                href="tel:090-120400"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.1 }}
                className="flex items-center gap-3 text-xl text-white"
              >
                <Phone className="w-5 h-5" />
                090-12 04 00
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

